import React, { FC, useEffect, useMemo, useState } from "react";
import { useRecoilState } from "recoil";
import { styled } from "@mui/system";
import { Checkbox } from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid";

import { getOutreachUsers } from "../../services/firebase";
import { onboardingFormAtom } from "../../state/atoms";
import { OutreachUser, OutreachDisplayUser } from "../../types/users";
import { COLORS, RADII } from "../../common/consts";
import Text, { TextVariant } from "../../components/Display/Text";
import DataGrid, {
  DataGridCol,
  DataGridRow,
} from "../../components/Display/DataGrid";
import Loading from "../../components/Display/Loading";
import Dialog from "../../components/Display/Dialog";

const MAX_LICENSES_ONBOARDING = 5;

// Helper function to get user initials
const getUserInitials = (fullName: string): string => {
  const parts = fullName.trim().split(" ");
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return fullName.substring(0, 2).toUpperCase();
};

const StyledValue = styled("span")`
  color: var(--color-integrationDisconnected, ${COLORS.integrationDisconnected});
`;

const InviteUsers: FC = () => {
  const [onboardingForm, setOnboardingForm] =
    useRecoilState(onboardingFormAtom);
  const [loading, setLoading] = useState(false);
  const [outreachUsers, setOutreachUsers] = useState<
    DataGridRow<OutreachDisplayUser>[]
  >([]);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const selectedEmails: string[] = useMemo(
    () => onboardingForm.allowedOwnerEmails || [],
    [onboardingForm.allowedOwnerEmails]
  );

  // Filter for only active users
  const activeUsers = useMemo(
    () => outreachUsers.filter((user) => user.isActive),
    [outreachUsers]
  );

  // Sync selectedUsers with selectedEmails from form
  useEffect(() => {
    const emailToUserIdMap = new Map(
      outreachUsers.map((user) => [user.email.toLowerCase().trim(), user.id.toString()])
    );
    const userIds = new Set(
      selectedEmails
        .map((email) => emailToUserIdMap.get(email.toLowerCase().trim()))
        .filter(Boolean) as string[]
    );
    setSelectedUsers(userIds);
  }, [selectedEmails, outreachUsers]);

  const handleSelectionChange = (userId: string, email: string, checked: boolean) => {
    const newSelected = new Set(selectedUsers);
    
    if (checked) {
      // Check if we're at the limit
      if (newSelected.size >= MAX_LICENSES_ONBOARDING) {
        setIsDialogOpen(true);
        return;
      }
      newSelected.add(userId);
    } else {
      newSelected.delete(userId);
    }
    
    setSelectedUsers(newSelected);
    
    // Update form with emails
    const selectedUserEmails = activeUsers
      .filter((user) => newSelected.has(user.id.toString()))
      .map((user) => user.email);
    
    setOnboardingForm((onboardingForm) => ({
      ...onboardingForm,
      allowedOwnerEmails: selectedUserEmails,
    }));
  };

  const columns: DataGridCol<OutreachDisplayUser>[] = useMemo(
    () => [
      {
        field: "checkbox",
        headerName: "",
        width: 50,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams<OutreachDisplayUser>) => {
          const userId = params.row.id.toString();
          const isSelected = selectedUsers.has(userId);
          
          return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
              <Checkbox
                size="small"
                checked={isSelected}
                onChange={(e) => {
                  handleSelectionChange(userId, params.row.email, e.target.checked);
                }}
                disabled={!isSelected && selectedUsers.size >= MAX_LICENSES_ONBOARDING}
                sx={{
                  color: COLORS.borderDark,
                  "&.Mui-checked": {
                    color: COLORS.foregroundBrand,
                  },
                  "&.Mui-disabled": {
                    color: COLORS.borderTertiary,
                  },
                  "&:focus": {
                    outline: "none !important",
                    boxShadow: "none !important",
                  },
                  "&:focus-visible": {
                    outline: "none !important",
                    boxShadow: "none !important",
                  },
                  "&.Mui-focusVisible": {
                    outline: "none !important",
                    boxShadow: "none !important",
                  },
                  "&.Mui-focused": {
                    outline: "none !important",
                    boxShadow: "none !important",
                  },
                  "&:active": {
                    outline: "none !important",
                    boxShadow: "none !important",
                  },
                  "& .MuiSvgIcon-root": {
                    "&:focus": {
                      outline: "none !important",
                      boxShadow: "none !important",
                    },
                  },
                  "& .MuiTouchRipple-root": {
                    display: "none !important",
                  },
                }}
              />
            </div>
          );
        },
      },
      {
        field: "fullName",
        headerName: "User",
        flex: 2,
        renderCell: (params: GridRenderCellParams<OutreachDisplayUser>) => {
          const user = params.row;
          const initials = getUserInitials(user.fullName);
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                height: "100%",
                padding: "8px 0",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  border: `1px solid var(--color-borderDark, ${COLORS.borderDark})`,
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: COLORS.backgroundSecondary,
                  flexShrink: 0,
                }}
              >
                {user.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt={user.fullName}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: 500,
                      color: COLORS.foregroundSecondary,
                    }}
                  >
                    {initials}
                  </span>
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px",
                }}
              >
                <span style={{ fontSize: "14px", fontWeight: 500 }}>
                  {user.fullName}
                </span>
                <span
                  style={{
                    fontSize: "12px",
                    color: COLORS.foregroundSecondary,
                  }}
                >
                  {user.email}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        field: "isActive",
        headerName: "Status",
        flex: 1,
        renderCell: (params: GridRenderCellParams<OutreachDisplayUser>) => {
          const isActive = params.row.isActive;
          return (
            <span
              style={{
                backgroundColor: isActive
                  ? "rgba(76, 161, 138, 0.1)"
                  : "rgba(182, 61, 79, 0.1)",
                color: isActive
                  ? COLORS.chartEmailValid
                  : COLORS.chartEmailInvalid,
                padding: "4px 12px 4px 8px",
                borderRadius: RADII.full,
                fontSize: "12px",
                fontWeight: 500,
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <span
                style={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  backgroundColor: isActive
                    ? COLORS.chartEmailValid
                    : COLORS.chartEmailInvalid,
                  display: "inline-block",
                }}
              />
              {isActive ? "Active" : "Inactive"}
            </span>
          );
        },
      },
      {
        field: "role",
        headerName: "Profile",
        flex: 1,
        valueGetter: (value: any, row: OutreachDisplayUser) =>
          row.role || "—",
      },
      {
        field: "teams",
        headerName: "Teams",
        flex: 1,
        valueGetter: (value: any, row: OutreachDisplayUser) =>
          row.teams?.join(", ") || "—",
      },
    ],
    [selectedUsers]
  );

  useEffect(() => {
    const fetchOutreachUsers = async () => {
      try {
        setLoading(true);
        const { result, status } = await getOutreachUsers();

        if (status === 401) {
          console.warn("⚠️ Organization not authorized for Outreach.");
          setOutreachUsers([]);
          return;
        }

        if (status === 200 && result?.users) {
          // Parse included resources (profiles, teams)
          const includedResources = result.included || [];
          
          const profilesMap = new Map();
          const teamsMap = new Map();

          includedResources.forEach((resource: any) => {
            if (resource.type === "profile") {
              profilesMap.set(resource.id, resource);
            } else if (resource.type === "team") {
              teamsMap.set(resource.id, resource);
            }
          });

          const transformedUsers: DataGridRow<OutreachDisplayUser>[] =
            result.users.map((user: OutreachUser) => {
              const attributes = user.attributes || {};
              const email = attributes.email || "";
              const firstName = attributes.firstName || "";
              const lastName = attributes.lastName || "";
              const name = attributes.name || "";
              const fullName =
                firstName && lastName
                  ? `${firstName} ${lastName}`
                  : name || email || "Unknown User";

              // Extract profile from relationships (displayed as "Profile" column)
              let role: string | undefined;
              const profileRelationship = user.relationships?.profile?.data;
              if (profileRelationship && profilesMap.has(profileRelationship.id)) {
                const profileResource = profilesMap.get(profileRelationship.id);
                role =
                  profileResource.attributes?.name ||
                  profileResource.attributes?.title ||
                  undefined;
              }

              // Extract teams from relationships
              let teams: string[] | undefined;
              const teamsRelationship = user.relationships?.teams?.data;
              if (teamsRelationship) {
                const teamRefs = Array.isArray(teamsRelationship)
                  ? teamsRelationship
                  : [teamsRelationship];

                const teamNames = teamRefs
                  .map((teamRef: any) => {
                    if (teamRef?.id && teamsMap.has(teamRef.id)) {
                      const teamResource = teamsMap.get(teamRef.id);
                      return teamResource?.attributes?.name || null;
                    }
                    return null;
                  })
                  .filter(Boolean);

                if (teamNames.length > 0) {
                  teams = teamNames;
                }
              }

              // Determine if user is active
              let isActive: boolean;
              if (attributes.locked !== undefined) {
                isActive = !attributes.locked;
              } else if (attributes.active !== undefined) {
                isActive = attributes.active;
              } else {
                // Default to active if field not available
                isActive = true;
              }

              return {
                id: user.id,
                email,
                firstName,
                lastName,
                fullName,
                avatarUrl: attributes.avatarUrl,
                isActive,
                isWhitelisted: false, // Not relevant during onboarding
                role,
                teams,
              };
            });

          setOutreachUsers(transformedUsers);
        }
      } catch (error) {
        console.error("Error fetching Outreach users", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOutreachUsers();
  }, []);

  return (
    <div className="d-flex flex-column gap-3" style={{ width: "100%" }}>
      <div className="d-flex flex-column gap-3">
        <Text variant={TextVariant.h6} className="text-center">
          Users with access
        </Text>
        <Text variant={TextVariant.sm} className="text-center">
          Select up to {MAX_LICENSES_ONBOARDING} active Outreach users to assign licenses
        </Text>
        {selectedUsers.size > 0 && (
          <Text variant={TextVariant.sm} style={{ color: COLORS.foregroundBrand }}>
            {selectedUsers.size} of {MAX_LICENSES_ONBOARDING} licenses selected
          </Text>
        )}
      </div>
      {loading ? (
        <div className="w-100 py-4 d-flex justify-content-center">
          <Loading />
        </div>
      ) : activeUsers.length === 0 ? (
        <div className="w-100 py-4 d-flex justify-content-center">
          <Text variant={TextVariant.sm} style={{ color: COLORS.foregroundSecondary }}>
            No active Outreach users found
          </Text>
        </div>
      ) : (
        <div style={{ width: "100%", height: "400px" }}>
          <DataGrid
            rows={activeUsers}
            columns={columns}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            hideFooter
          />
        </div>
      )}
      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
        <Text variant={TextVariant.sm}>
          You can assign up to{" "}
          <StyledValue>{MAX_LICENSES_ONBOARDING} licenses</StyledValue> during onboarding.
          <br />
          Please contact support to upgrade your plan.
        </Text>
      </Dialog>
    </div>
  );
};

export default InviteUsers;
